import { cn } from '@/lib/utils';
import { useEffect, useState, useRef } from 'react';
import Coin from '@/assets/icons/coin.svg?react';

const MAX_COINS = 6;

export function CoinRain(props: React.HTMLAttributes<HTMLDivElement>) {
  const [coins, setCoins] = useState<ReturnType<typeof createCoin>[]>([]);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const generateCoins = () => {
      setCoins((prevCoins) => {
        // Remove coins that have finished their animation
        const previousCoinsToKeep = prevCoins.filter(
          (coin) => Date.now() - (coin.createdAt + coin.delay * 1000) < coin.duration * 1000,
        );
        // get how many coins have moved passed half screen to add that many more on top of max coins
        const numCoinsHalfPassedTheScreen = previousCoinsToKeep.filter(
          (coin) => Date.now() - (coin.createdAt + coin.delay * 1000) > (coin.duration * 1000) / 2,
        ).length;

        return [
          ...previousCoinsToKeep,
          ...Array(MAX_COINS + numCoinsHalfPassedTheScreen - previousCoinsToKeep.length)
            .fill(null)
            .map((_, idx) => createCoin(idx)),
        ];
      });
    };

    const interval = setInterval(generateCoins, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      id="coin-rain"
      ref={ref}
      className={cn(
        'absolute top-0 left-0 h-full w-full overflow-hidden bg-transparent',
        props.className,
      )}
    >
      <div className="relative h-full w-full">
        {coins.map((coin) => (
          <div
            key={coin.id}
            className="animate-fall absolute -translate-y-96"
            style={{
              width: `${coin.size}px`,
              height: `${coin.size}px`,
              left: `${coin.left}%`,
              animationDuration: `${coin.duration}s`,
              animationDelay: `${coin.delay}s`,
            }}
          >
            <Coin />
          </div>
        ))}
      </div>
    </div>
  );
}

function createCoin(idx: number) {
  const id = Math.random().toString(36).substring(2, 9);
  return {
    id,
    size: Math.random() * 20 + 50, // Random size between 50px and 70px
    left: idx % 2 === 0 ? Math.random() * 45 : Math.random() * 25 + 55, // Left half for even, right half for odd
    delay: Math.random() * 2, // Random delay between 0s and 2s
    duration: Math.random() * 2 + 4, // Random duration between 4s and 6s
    createdAt: Date.now(),
  };
}
